import * as S from './styles';
import { useState } from 'react';
import { useSelector, shallowEqual } from 'react-redux';
import { RootReducer } from 'redux/reducers';
import { Frame, CommentFrameEntry } from 'dragonmouse-shared/interfaces';
import { IconType } from 'components/Icon';
import MultilineInput from 'components/Widget/Input/MultilineInput';
import SubmitButton, { SubmitButtonState } from 'components/Widget/Button/SubmitButton';
import forge from 'apis/forge';
import { v4 as uuid } from 'uuid';
import { trackOutputAction } from 'analytics';
import { canEditFrame } from 'redux/db/util';
import { displayViewOnlyModal } from 'redux/mode/util';

interface CommentInputProps {
	frame: Frame;
	elementId?: string;
	readonly?: boolean;
	parentId?: string;
	autoFocus?: boolean;
	onCancel?: () => void;
	onSubmit?: () => void;
}

const CommentInput = ({
	frame,
	elementId,
	readonly = false,
	parentId,
	autoFocus,
	onCancel,
	onSubmit: onSubmitProp,
}: CommentInputProps) => {
	const [comment, setComment] = useState<string>('');
	const [isFocused, setIsFocused] = useState<boolean>(false);

	const { authUser } = useSelector(
		(r: RootReducer) => ({
			authUser: r.mode.authUser,
		}),
		shallowEqual
	);

	//----------------------------------------------------------------------
	// HANDLERS
	//----------------------------------------------------------------------
	// forge.addFrameEntry is an optimistic write — the new comment appears in the
	// UI synchronously via Redux, so there's no loading state to manage here.
	const onSubmit = () => {
		if (!comment.trim() || !authUser || readonly) return;

		if (!canEditFrame(frame)) {
			displayViewOnlyModal();
			return;
		}

		trackOutputAction('add_comment');
		const newComment: CommentFrameEntry = {
			id: uuid(),
			kind: 'comment',
			message: comment.trim(),
			userId: authUser.id,
			createdAt: new Date(),
			deletedAt: null,
			elementId: elementId ?? null,
			siteId: frame.siteId ?? undefined,
			resolvedAt: null,
			parentId: parentId ?? null,
		};

		forge.addFrameEntry({
			frameId: frame.id,
			entry: newComment,
		});
		setComment('');
		onSubmitProp?.();
	};

	const onChangeComment = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
		setComment(e.target.value);
	};

	const onKeyDown = (e: React.KeyboardEvent<HTMLElement>) => {
		if (e.key === 'Enter' && !e.shiftKey) {
			e.preventDefault();
			onSubmit();
		} else if (e.key === 'Escape' && onCancel) {
			e.preventDefault();
			onCancel();
		}
	};

	//----------------------------------------------------------------------
	// RENDERING
	//----------------------------------------------------------------------
	const renderActions = () => {
		const hasInput = comment.trim().length > 0;
		return (
			<S.Actions>
				<S.Spacer />
				<S.SubmitButtonWrapper>
					<SubmitButton
						state={hasInput ? SubmitButtonState.REGULAR : SubmitButtonState.DISABLED}
						type={IconType.LINE_ARROW_UP}
						tooltip="Add comment"
						tooltipPlacement="bottom-start"
						onClick={onSubmit}
					/>
				</S.SubmitButtonWrapper>
			</S.Actions>
		);
	};

	return (
		<S.Base>
			{readonly ? (
				<S.ReadOnlyText>
					You're in view-only mode. To add comments, ask the owner for editing permissions.
				</S.ReadOnlyText>
			) : (
				<S.InputArea isFocused={isFocused}>
					<S.MultilineInputWrapper>
						<MultilineInput
							value={comment}
							onInput={onChangeComment}
							onKeyDown={onKeyDown}
							placeholder={parentId ? 'Reply...' : 'Add a comment...'}
							minLines={2}
							renderBorder={false}
							autoFocus={autoFocus}
							onFocusChange={(isFocused) => {
								setIsFocused(isFocused);
							}}
						/>
					</S.MultilineInputWrapper>
					{renderActions()}
				</S.InputArea>
			)}
		</S.Base>
	);
};

export default CommentInput;
